import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { http } from "@/lib/api";
import FarmersNav from "@/components/FarmersNav";
import { ArrowLeft, MapTrifold, Funnel } from "@phosphor-icons/react";

/**
 * FarmersMap — /farmers/map
 *
 * World map of farmer observations with category + time filters. Reuses
 * the public farmer_reports (approved) + `share_with_naturepulse` mirrored
 * observations. All coordinates rounded to a 2-km grid server-side.
 */

const CATEGORY_CHOICES = [
  { key: "", label: "All" },
  { key: "crop", label: "Crops" },
  { key: "pest", label: "Pests" },
  { key: "disease", label: "Diseases" },
  { key: "weather", label: "Weather" },
  { key: "water", label: "Water" },
  { key: "wildlife", label: "Wildlife" },
  { key: "pollinators", label: "Pollinators" },
];
const DAYS_CHOICES = [7, 14, 30, 90, 180];

const CAT_COLORS = {
  crop: "#65a30d",
  pest: "#c2410c",
  disease: "#be123c",
  weather: "#0284c7",
  water: "#0891b2",
  wildlife: "#7c3aed",
  pollinators: "#eab308",
  default: "#78716c",
};

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 3); }, [center, map]);
  return null;
}

export default function FarmersMap() {
  const [category, setCategory] = useState("");
  const [days, setDays] = useState(30);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    p.set("days", String(days));
    p.set("limit", "500");
    http.get(`/farmers/reports/public?${p.toString()}`)
      .then(({ data }) => setPins(data.reports || []))
      .catch(() => setPins([]))
      .finally(() => setLoading(false));
  }, [category, days]);

  const withCoords = useMemo(
    () => pins.filter((p) => p.latitude != null && p.longitude != null),
    [pins],
  );

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-map-page">
      <FarmersNav />
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 mt-6">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <MapTrifold size={12} weight="fill" /> / Farm Pulse Map
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
            What are farmers seeing around the world?
          </h1>
          <p className="mt-2 font-serif-display italic text-[#3A3830]">
            Live observations from farmers. Filter by crop, pest, weather or wildlife to reveal patterns.
          </p>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="farmers-map-filters">
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830] self-center inline-flex items-center gap-1">
            <Funnel size={11} weight="fill" /> Category:
          </span>
          {CATEGORY_CHOICES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`farmers-map-cat-${c.key || "all"}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${
                category === c.key ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white text-[#3A3830] border-black/15 hover:border-black/40"
              }`}
            >
              {c.label}
            </button>
          ))}
          <span className="w-2" />
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830] self-center">Time:</span>
          {DAYS_CHOICES.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`farmers-map-days-${d}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${
                days === d ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white text-[#3A3830] border-black/15 hover:border-black/40"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>

        {/* Map */}
        <div className="mt-6 rounded-2xl border border-black/10 bg-white overflow-hidden h-[560px] relative" data-testid="farmers-map-canvas">
          <MapContainer center={[15, 20]} zoom={3} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Recenter center={[15, 20]} />
            {withCoords.map((p) => {
              const color = CAT_COLORS[p.category] || CAT_COLORS.default;
              return (
                <CircleMarker
                  key={p.id}
                  center={[p.latitude, p.longitude]}
                  radius={6}
                  pathOptions={{ color, fillColor: color, fillOpacity: 0.6, weight: 1 }}
                >
                  <Popup>
                    <div className="text-[13px]">
                      <div className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#B85F2A]">
                        {p.category}{p.severity ? ` · ${p.severity}` : ""}
                      </div>
                      <div className="font-serif-display text-base leading-tight mt-1">{p.title || "Observation"}</div>
                      {p.description && <div className="mt-1 text-[12px]">{String(p.description).slice(0, 200)}</div>}
                      {p.location_label && <div className="mt-1 text-[11px] text-[#666]">{p.location_label}</div>}
                      <div className="mt-1 text-[10px] text-[#999]">{p.created_at?.slice(0, 10)}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
          {loading && (
            <div className="absolute top-3 left-3 rounded-full bg-white/90 border border-black/15 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase">
              Loading…
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-[#3A3830]">
          <div className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-lime-600" /> Crops</div>
          <div className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-orange-700" /> Pests</div>
          <div className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-rose-700" /> Diseases</div>
          <div className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-sky-600" /> Weather</div>
          <div className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-violet-600" /> Wildlife</div>
          <div className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-yellow-500" /> Pollinators</div>
          <div className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
            {withCoords.length} of {pins.length} pins have coordinates
          </div>
        </div>
      </div>
    </main>
  );
}
